/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { t } from 'i18next';

// Component imports
import { Button, Card, Col, Empty, Form, Row, Select, Spin, Tooltip, Typography } from 'antd';
import { InfoCircleTwoTone } from '@ant-design/icons';
import RichTextEditor from '../Forms/Fields/RichTextEditor/RichTextEditor';
import Prompt from '../UI/Prompt';
import TextAreaWithCopyButton from '../Forms/Fields/TextAreaWithCopyButton';

// Misc imports
import { useFetchAutomatedAnswersQuery, useUpdateAutomatedAnswerMutation } from '../../store/automatedAnswers/automatedAnswersApi';
import { useFetchBotQuery } from '../../store/bots/botsApi';
import { DEFAULT_FALLBACK_ID, NODE_TYPES } from '../../constants/flowGraph';
import { useFetchActivitiesQuery, useUpdateActivityMutation } from '../../store/activities/activitiesApi';
import clone from '../../utils/object/clone';
import { useFetchBotDeploymentsQuery } from '../../store/botDeployments/botDeploymentsApi';
import { getDeploymentLibraryScript, getDeploymentSnippet } from '../../utils/misc/getDeploymentSnippet';
import { BOT_DEPLOYMENTS_CHANNELS } from '../../constants/main';
import { SUPPORT_EMAIL } from '../../constants/env';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';

const HRWizPreferences = () => {
  const { orgId, tenantId } = useParams();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();

  // API state
  const { data: bot, isLoading: isLoadingBot } = useFetchBotQuery({ orgId, tenantId }, { skip: !tenantId });
  const { data: automatedAnswers, isLoading: isLoadingAA } = useFetchAutomatedAnswersQuery({ orgId, tenantId }, { skip: (!tenantId || !bot) });
  const { data: activities, isLoading: isLoadingActivities } = useFetchActivitiesQuery({ orgId, tenantId }, { skip: (!tenantId || !bot) });
  const { data: deployments, isLoading: isLoadingDeployments } = useFetchBotDeploymentsQuery({ orgId, tenantId }, { skip: (!tenantId || !bot) });
  const [updateAA, { isLoading: isUpdatingAutomatedAnswer }] = useUpdateAutomatedAnswerMutation();
  const [updateActivity, { isLoading: isUpdatingActivity }] = useUpdateActivityMutation();
  const primaryBotLanguage = bot?.settings?.languages?.primary || 'en';

  // Local state
  const [defaultResponse, setDefaultResponse] = useState(null);
  const [welcomeMessageActivity, setWelcomeMessageActivity] = useState(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [displayDeployments, setDisplayDeployments] = useState([]);
  const [selectedDeployment, setSelectedDeployment] = useState(null);

  useEffect(() => {
    if (!deployments) {
      return;
    }
    const _displayDeployments = deployments.items.filter(deployment => !deployment.isPreview);
    setDisplayDeployments(_displayDeployments);
    setSelectedDeployment(_displayDeployments[0]);
  }, [deployments]);

  useEffect(() => {
    if (!automatedAnswers) {
      return;
    }

    setDefaultResponse(automatedAnswers.items.find(automatedAnswer => automatedAnswer.id === DEFAULT_FALLBACK_ID) ?? {});
  }, [automatedAnswers]);

  useEffect(() => {
    if (!activities) {
      return;
    }

    setWelcomeMessageActivity(activities.items.find(activity => activity.content.nodes.find(node => node.id === 'welcome-non-admin')) ?? {});
  }, [activities]);

  useEffect(() => {
    form.resetFields();
    setHasPendingChanges(false);
  }, [welcomeMessageActivity?.id, defaultResponse?.id, searchParams.get('tab')]);

  const onSubmit = async (values) => {
    // Formatting the default flow value
    let clonedDefaultResponse = clone(defaultResponse);
    clonedDefaultResponse.content.nodes.find(node => node.type === NODE_TYPES.MESSAGE).label[primaryBotLanguage] = values.defaultResponse;

    // Formatting the welcome message value
    let clonedWelcomeMessage = clone(welcomeMessageActivity);
    clonedWelcomeMessage.content.nodes.find(node => node.type === NODE_TYPES.MESSAGE).label[primaryBotLanguage] = values.welcomeMessage;
    clonedWelcomeMessage = {
      content: {
        ...clonedWelcomeMessage.content,
        nodes: clonedWelcomeMessage.content.nodes,
        edges: clonedWelcomeMessage.content.edges
      },
      settings: {
        ...clonedWelcomeMessage.settings
      }
    };

    const res1 = await updateActivity({ orgId, tenantId, activityId: welcomeMessageActivity.id, data: clonedWelcomeMessage, doNotInvalidate: false, successMessage: { showMessage: false } }).unwrap();
    const res2 = await updateAA({ orgId, tenantId, automatedAnswerId: clonedDefaultResponse.id, data: clonedDefaultResponse, successMessage: { showMessage: false } }).unwrap();

    if (res1 && res2) {
      openConsoleAlertMessage({ type: 'success', message: t('Preferences updated') });
      setHasPendingChanges(false);
    }
  };

  const libraryScript = getDeploymentLibraryScript().trim();
  const script = getDeploymentSnippet({ deploymentId: selectedDeployment?.id }).trim();

  return (
    <Fragment>
      <Card
        className="overflow-hidden p-0"
        bodyStyle={{ height: '100%', overflow: 'hidden' }}
      >
        <Prompt
          when={hasPendingChanges}
          onConfirm={() => form.submit()}
          okText={t('Save Changes')}
          cancelText={t('Quit without saving')}
          title={t('Save Changes?')}
          message={t('Your unsaved changes will be lost. Save changes before closing?')}
        />

        {
          isLoadingBot || isLoadingAA || isLoadingActivities ? (
            <Row className="h-100 py-3" align="middle" justify="center">
              <Spin />
            </Row>
          ) : (
            <Form
              form={form}
              className="d-flex flex-column flex-auto h-100"
              onFinish={onSubmit}
              onFieldsChange={() => setHasPendingChanges(true)}
              initialValues={{
                welcomeMessage: welcomeMessageActivity?.content?.nodes?.find(node => node.type === NODE_TYPES.MESSAGE)?.label?.[primaryBotLanguage] ?? '',
                defaultResponse: defaultResponse?.content?.nodes?.find(node => node.type === NODE_TYPES.MESSAGE)?.label?.[primaryBotLanguage] ?? ''
              }}
            >
              <div className="overflow-x-hidden overflow-y-auto py-5 px-6 h-100">
                <Typography.Paragraph
                  className="fw-500 bg-body border-left border-top border-bottom border-right p-3 mb-6 border-radius-base"
                >
                  {t('This is HRwiz v1.2 - a Language AI that is trained with questions typically asked to HR.')}

                  <Typography.Paragraph className="mb-0">
                    {t('You can tailor its knowledge by uploading HR documents or adding articles and FAQs.')}
                  </Typography.Paragraph>
                </Typography.Paragraph>

                <Typography.Paragraph className="fw-bold">
                  {t('Welcome Message')}

                  <Tooltip
                    mouseEnterDelay={0.5}
                    title={t('This message is shown to employees upon installation of HRwiz on Microsoft Teams or Slack, or when the chat is opened on your site.')}
                  >
                    <InfoCircleTwoTone className="ml-1" />
                  </Tooltip>
                </Typography.Paragraph>

                <Form.Item name="welcomeMessage">
                  <RichTextEditor
                    placeholder="Enter a welcome message"
                  />
                </Form.Item>

                <Typography.Paragraph className="fw-bold">
                  {t('Default Flow')}

                  <Tooltip
                    mouseEnterDelay={0.5}
                    title={t('This response is used when the Language AI cannot find a relevant answer amongst the articles and FAQs.')}
                  >
                    <InfoCircleTwoTone className="ml-1" />
                  </Tooltip>
                </Typography.Paragraph>

                <Form.Item name="defaultResponse">
                  <RichTextEditor
                    placeholder="Enter a default flow"
                  />
                </Form.Item>

                <Typography.Paragraph className="fw-500 bg-body border-left border-top border-bottom border-right p-3 mb-6 border-radius-base">
                  {t('Once you updated its knowlegde, have HRwiz join any of your group chats on MS Teams or Slack, or put HRwiz chat on your internal site using JavaScript.')}

                  <Typography.Paragraph className="mb-0">
                    {t('Questions? Contact ')}
                    <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                  </Typography.Paragraph>
                </Typography.Paragraph>
              </div>

              <div className="p-4 border-top w-100">
                <Button
                  size="large"
                  className="d-block float-right"
                  type="primary"
                  disabled={!hasPendingChanges}
                  loading={isUpdatingActivity || isUpdatingAutomatedAnswer}
                  onClick={() => form.submit()}
                >
                  {t('Save Changes')}
                </Button>
              </div>
            </Form>
          )
        }
      </Card>

      <Card
        className="overflow-hidden p-0 mt-4"
        bodyStyle={{ height: '100%', overflow: 'hidden' }}
      >
        {
          isLoadingDeployments ? (
            <Row className="h-100 py-3" align="middle" justify="center">
              <Spin />
            </Row>
          ) : !isLoadingDeployments && displayDeployments.length === 0 ? (
            <Row className="h-100 pt-4" align="middle" justify="center">
              <Empty
                description={(
                  <Typography.Paragraph>
                    {t('No deployments are available')}
                  </Typography.Paragraph>
                )}
              />
            </Row>
          ) : (
            <div className="overflow-x-hidden overflow-y-auto py-5 px-6 h-100">
              <Typography.Title level={5} className="fw-bold">
                {t('Deploy your Agent')}
              </Typography.Title>

              <Typography.Text className="text-muted">
                {t('Select a deployment below')}
              </Typography.Text>

              <Row gutter={24}>
                <Col span={24}>
                  <Select
                    value={selectedDeployment?.id}
                    className="w-100 mb-3 mt-2"
                    onChange={(value) => setSelectedDeployment(displayDeployments.find(tmp => tmp.id === value))}
                    options={displayDeployments?.map(tmp => ({ label: tmp.displayName, value: tmp.id }))}
                  />
                </Col>

                <Col span={24}>
                  <Typography.Title level={5} className="fw-bold mt-4">
                    {
                      selectedDeployment?.platform === BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_WEB_CHAT.key
                        ? t('JavaScript Snippet')
                        : Object.values(BOT_DEPLOYMENTS_CHANNELS)?.find(channel => channel.key)?.name || ''
                    }
                  </Typography.Title>

                  {
                    selectedDeployment?.platform === BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_WEB_CHAT.key ? (
                      <Fragment>
                        <Row className="mb-5" gutter={24}>
                          <Col span={24}>
                            <Typography.Text className="text-muted">
                              {t('Insert this code at the <HEAD> section on the webpage where the agent is to be deployed.')}
                            </Typography.Text>
                          </Col>

                          <Col span={24}>
                            <TextAreaWithCopyButton
                              readOnly
                              autoSize={{ minRows: 2, maxRows: 32 }}
                              className="text-code"
                              value={libraryScript}
                              spellCheck={false}
                            />
                          </Col>
                        </Row>
                        <Row gutter={24}>
                          <Col span={24}>
                            <Typography.Text className="text-muted">
                              {t('Insert this code at the end of the <BODY> section on the webpage where the agent is to be deployed.')}
                            </Typography.Text>
                          </Col>

                          <Col span={24}>
                            <TextAreaWithCopyButton
                              readOnly
                              autoSize={{ minRows: 2, maxRows: 32 }}
                              className="text-code"
                              value={script}
                              spellCheck={false}
                            />
                          </Col>
                        </Row>
                      </Fragment>
                    ) : (
                      <Typography.Text className="text-muted">
                        {t('No information available for this deployment.')}
                      </Typography.Text>
                    )
                  }
                </Col>
              </Row>
            </div>
          )
        }
      </Card>
    </Fragment>
  );
};

export default HRWizPreferences;
